import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './DropdownMenu.module.css';

export interface DropdownItem {
    label: string;
    icon?: React.ReactNode;
    value?: any;
    onClick: (e: React.MouseEvent) => void;
    isSelected?: boolean;
    /** Shown but not clickable; `hint` says why. */
    disabled?: boolean;
    /** A second line under the label (e.g. why an item is disabled). */
    hint?: string;
    /** A destructive action (red). */
    danger?: boolean;
    /** A line above this item, starting a new group. */
    dividerBefore?: boolean;
}

interface DropdownMenuProps {
    isOpen: boolean;
    onClose: () => void;
    items: DropdownItem[];
    className?: string;
    triggerRef?: React.RefObject<HTMLElement> | { current: HTMLElement | null };
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    position?: 'bottom' | 'right' | 'top';
    align?: 'left' | 'right';
    matchTriggerWidth?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
    isOpen, onClose, items, className, triggerRef,
    onMouseEnter, onMouseLeave,
    position = 'bottom',
    align = 'left',
    matchTriggerWidth = false
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState<{ top: number; left: number; width: number | 'auto' }>({
        top: 0,
        left: 0,
        width: 'auto'
    });

    // `align` names the trigger edge the menu lines up with in left-to-right
    // languages; right-to-left mirrors it, so the menu still opens into the page.
    const rtl = typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl';
    const edge: 'left' | 'right' = rtl ? (align === 'right' ? 'left' : 'right') : align;

    useLayoutEffect(() => {
        if (isOpen && triggerRef && 'current' in triggerRef && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const targetWidth = matchTriggerWidth ? rect.width : 'auto';

            if (position === 'top') {
                setCoords({
                    top: rect.top - 10,
                    left: edge === 'right' ? rect.right : rect.left,
                    width: targetWidth
                });
            } else if (position === 'right') {
                setCoords({
                    top: window.innerHeight - 300,
                    left: rect.right + 4,
                    width: targetWidth
                });
            } else {
                setCoords({
                    top: rect.bottom + 4,
                    left: edge === 'right' ? rect.right : rect.left,
                    width: targetWidth
                });
            }
        }
    }, [isOpen, triggerRef, position, edge, matchTriggerWidth]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                triggerRef && 'current' in triggerRef && triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', onClose);
            window.addEventListener('resize', onClose);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', onClose);
            window.removeEventListener('resize', onClose);
        };
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen) return null;

    let transformStyle: React.CSSProperties = {};
    if (position === 'top') {
        transformStyle = { transform: `translateY(-100%) ${edge === 'right' ? 'translateX(-100%)' : ''}` };
    } else if (position === 'right') {
        transformStyle = { transform: 'translateY(-50%)' };
    } else if (edge === 'right') {
        transformStyle = { transform: 'translateX(-100%)' };
    }

    return createPortal(
        <div
            className={`${styles.menu} ${className || ''}`}
            ref={menuRef}
            style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                width: coords.width === 'auto' ? 'auto' : `${coords.width}px`,
                ...transformStyle
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {items.map((item, index) => (
                <React.Fragment key={index}>
                {item.dividerBefore && index > 0 && <div className={styles.divider} role="separator" />}
                <div
                    role="menuitem"
                    tabIndex={item.disabled ? -1 : 0}
                    aria-disabled={item.disabled || undefined}
                    title={item.disabled && item.hint ? item.hint : undefined}
                    className={`${styles.item} ${item.isSelected ? styles.selectedItem : ''} ${item.disabled ? styles.disabledItem : ''} ${item.danger ? styles.dangerItem : ''} subtitle-2`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (item.disabled) return;
                        item.onClick(e);
                        onClose();
                    }}
                    onKeyDown={(e) => {
                        if (item.disabled || (e.key !== 'Enter' && e.key !== ' ')) return;
                        e.preventDefault();
                        item.onClick(e as unknown as React.MouseEvent);
                        onClose();
                    }}
                >
                    {item.isSelected !== undefined && (
                        <div className={`${styles.radioCircle} ${item.isSelected ? styles.radioActive : ''}`}>
                            {item.isSelected && <div className={styles.radioInner} />}
                        </div>
                    )}

                    {item.icon && <span className={styles.icon}>{item.icon}</span>}
                    {item.hint ? (
                        <span className={styles.labelStack}>
                            <span className={styles.label}>{item.label}</span>
                            <span className={styles.hint}>{item.hint}</span>
                        </span>
                    ) : (
                        <span className={styles.label}>{item.label}</span>
                    )}
                </div>
                </React.Fragment>
            ))}
        </div>,
        document.body
    );
};